import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';

interface SousBien {
  id: number;
  name: string;
  [key: string]: any;
}

interface Bien {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  agencyName?: string;
  status?: string;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  sous_biens?: SousBien[];
}

@Component({
  selector: 'app-list-agence-bien',
  templateUrl: './list-agence-bien.component.html',
  styleUrls: ['./list-agence-bien.component.scss']
})
export class ListAgenceBienComponent implements OnInit {
  idAgency!: number;
  Agency: any;
  list: Bien[] = [];
  isLoading = false;

  constructor(
    private biensService: BiensImmobsBiensImmobs,
    private agencieService: AgenciesService,
    private sousBienService: SousBienService,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    this.idAgency = idParam ? +idParam : 0;

    if (this.idAgency <= 0) {
      Swal.fire({
        title: 'Erreur',
        text: 'ID d’agence manquant ou invalide.',
        icon: 'error'
      });
      this.location.back();
      return;
    }

    this.agencieService.setIdAgency(this.idAgency);
    this.loadAgencyDetails();
  }

  loadAgencyDetails(): void {
    this.isLoading = true;
    this.agencieService.getAgencieById(this.idAgency).subscribe({
      next: (data: any) => {
        this.Agency = data;
        const biens = Array.isArray(data?.biens) ? data.biens : [];
        this.list = biens.map((b: Bien) => ({ ...b, sous_biens: [] }));

        // Initialize order indexes and sort by displayOrder if present
        this.initializeOrderIndexes();

        if (this.list.length > 0) {
          this.loadSousBiensForBiens();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les biens de l’agence.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.list.some(b => b.displayOrder !== undefined && b.displayOrder !== null);
    if (hasOrder) {
      // Sort by displayOrder ascending
      this.list.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      // Fallback to createdAt (oldest first)
      this.list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }
    // Assign sequential orderIndex for UI
    this.list.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  private loadSousBiensForBiens(): void {
    const requests = this.list.map(bien => this.sousBienService.getBiensById(bien.id));
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((fullBien, index) => {
          this.list[index].sous_biens = fullBien?.sous_biens || [];
        });
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erreur lors du chargement des sous‑biens individuels :', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Attention',
          text: 'Impossible de charger certains sous‑biens.',
          icon: 'warning',
          timer: 2000,
          showConfirmButton: false
        });
      }
    });
  }

  // ============================================================
  // ORDERING METHODS (Batch update)
  // ============================================================

  private saveAllOrdersBatch(): void {
    if (this.list.length === 0) return;
    this.isLoading = true;
    const updates = this.list.map(bien => ({
      id: bien.id,
      displayOrder: bien.displayOrder!
    }));
    this.biensService.updateBiensOrderBatch(updates).subscribe({
      next: () => {
        this.isLoading = false;
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success',
          title: 'Ordre mis à jour'
        });
      },
      error: (err) => {
        console.error('Batch update error:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
          icon: 'error'
        });
        this.loadAgencyDetails(); // reload to revert
      }
    });
  }

  private updateOrdersAndSave(): void {
    this.list.forEach((bien, idx) => {
      bien.orderIndex = idx + 1;
      bien.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  drop(event: CdkDragDrop<Bien[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.list, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.list[index], this.list[index - 1]] = [this.list[index - 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.list.length - 1) return;
    [this.list[index], this.list[index + 1]] = [this.list[index + 1], this.list[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE
  // ============================================================

  deleteAgenciesProjet(bien: Bien): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le !'
    }).then((result) => {
      if (result.isConfirmed) {
        this.biensService.deleteBiensImmobs(bien.id).subscribe({
          next: () => {
            Swal.fire('Supprimé !', 'Le bien a été supprimé.', 'success');
            this.list = this.list.filter(b => b.id !== bien.id);
            this.updateOrdersAndSave(); // re-index after deletion
          },
          error: () => {
            Swal.fire('Erreur', 'La suppression a échoué.', 'error');
          }
        });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }
}